import { Controller } from "@hotwired/stimulus"

/**
 * SpinShot Features Parallax Controller
 * 
 * Creates a scroll-triggered parallax effect where:
 * - Images stack in the left column and change as you scroll
 * - Text items in the right column fade in/out based on scroll position
 * 
 * Usage: Add data-controller="spinshot-features" to the container
 */
export default class extends Controller {
    static targets = ["image", "text", "container", "parallax"]

    connect() {
        this.handleScroll = this.handleScroll.bind(this)
        window.addEventListener('scroll', this.handleScroll, { passive: true })

        // Initial check
        this.handleScroll()
    }

    disconnect() {
        window.removeEventListener('scroll', this.handleScroll)
    }

    handleScroll() {
        if (!this.hasContainerTarget) return

        const windowHeight = window.innerHeight
        const featureCount = this.textTargets.length

        // Parallax effect for image container - scroll 20% faster than normal
        if (this.hasParallaxTarget) {
            const containerRect = this.containerTarget.getBoundingClientRect()
            const scrolledIntoView = Math.max(0, -containerRect.top)

            // Move images 20% faster (1.2x speed) - slower to reduce white space
            const parallaxSpeed = 0.2
            const translateY = -(scrolledIntoView * parallaxSpeed)

            this.parallaxTarget.style.transform = `translateY(${translateY}px)`
        }

        // Check each text item's position to determine active state
        let activeIndex = 0
        let closestDistance = Infinity

        this.textTargets.forEach((text, index) => {
            const rect = text.getBoundingClientRect()
            const itemCenter = rect.top + rect.height / 2
            const viewportCenter = windowHeight / 2
            const distance = Math.abs(itemCenter - viewportCenter)

            if (distance < closestDistance) {
                closestDistance = distance
                activeIndex = index
            }
        })

        // Update active states for text
        this.textTargets.forEach((text, index) => {
            if (index === activeIndex) {
                text.style.opacity = '1'
                text.style.color = 'rgb(35, 42, 54)'
                text.classList.add('active')
            } else {
                text.style.opacity = '0.3'
                text.style.color = 'rgb(101, 105, 112)'
                text.classList.remove('active')
            }
        })

        // Update active states for images only if not using parallax scroll
        // (For parallax sections, images scroll naturally without opacity changes)
        if (!this.hasParallaxTarget) {
            this.imageTargets.forEach((image, index) => {
                if (index === activeIndex) {
                    image.style.opacity = '1'
                    image.style.transform = 'scale(1)'
                    image.style.zIndex = '2'
                } else {
                    image.style.opacity = '0'
                    image.style.transform = 'scale(0.95)'
                    image.style.zIndex = '1'
                }
            })
        }
    }
}
